import React, {RefCallback, useEffect} from 'react';
import headerImg from './img/header.webp';
import {
  Button,
  Link as AnchorLink,
  makeStyles,
  SvgIcon,
  useMediaQuery,
} from '@material-ui/core';
import {Link as RouterLink, useHistory, useRouteMatch} from 'react-router-dom';
import {useSwipeable} from 'react-swipeable';
import {MainPages} from './NavPages';
import LightbulbTwoToneIcon from '@material-ui/icons/EmojiObjectsTwoTone';
import CalendarTwoToneIcon from '@material-ui/icons/EventAvailableTwoTone';
import LooksTwoToneIcon from '@material-ui/icons/LooksTwoTone';
import GradeTwoToneIcon from '@material-ui/icons/GradeTwoTone';
import BarChartTwoToneIcon from '@material-ui/icons/BarChartTwoTone';
import AccountCircleTwoToneIcon from '@material-ui/icons/AccountCircleTwoTone';
import AccountCircle from '@material-ui/icons/AccountCircle';
import {useUserContext} from './UserProvider';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    flexFlow: 'wrap',
    paddingRight: '10px',
    width: '100%',
    overflow: 'hidden',
    padding: '0px',
    '& a': {
      textTransform: 'none',
    },
    '& ul': {
      listStyle: 'none',
      padding: '0px',
      order: 1,
      '& li': {
        flexGrow: 1,
        display: 'flex',
        justifyContent: 'center',
      },
    },
    '& nav': {
      flexGrow: 1,
      maxWidth: '70em',
      '& ul': {
        display: 'flex',
        '& li': {
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
        },
      },
    },
    '& .MuiButton-root': {padding: '0px'},
    '& .MuiButton-label': {
      flexFlow: 'wrap',
      '& div': {
        textAlign: 'center',
        '& svg': {margin: '0px 0.5em', fontSize: '2em'},
      },
    },
  },
  button: {
    '& svg,div': {
      color: theme.palette.primary.main,
    },
  },
  buttonSel: {
    '& svg,div': {
      color: theme.palette.text.primary,
      fontWeight: 800,
      textDecoration: 'underline',
    },
  },
}));

type MenuItemProps = {
  page: number;
  icon: typeof SvgIcon;
};

const MenuItem: React.FC<MenuItemProps> = (props: MenuItemProps) => {
  const styles = useStyles();
  const vb = '0 -2 24 24';
  const path = MainPages[props.page].path;
  const isVeryNarrow = useMediaQuery('(max-width: 360px)');
  const routeMatch = useRouteMatch('/:page');
  const onPage = routeMatch?.url === path;
  const buttonStyle = onPage ? styles.buttonSel : styles.button;
  return (
    <li style={{order: 1}} className="navButton">
      <Button className={buttonStyle} component={RouterLink} to={path}>
        <div>
          <props.icon
            viewBox={vb}
            fontSize={props.page === 5 ? 'large' : 'medium'}
          />
        </div>
        <div style={{fontSize: isVeryNarrow ? '0.75em' : 'unset'}}>
          {MainPages[props.page].label}
        </div>
      </Button>
    </li>
  );
};

const AccountMenuItem: React.FC = () => {
  const {hasEmail} = useUserContext();
  return (
    <MenuItem
      page={5}
      icon={hasEmail ? AccountCircle : AccountCircleTwoToneIcon}
    />
  );
};

const Header: React.FC = () => {
  const styles = useStyles();
  const history = useHistory();
  const swipeableRef = useSwipeable({
    onSwiped: e => {
      const offset = e.dir === 'Left' ? 1 : e.dir === 'Right' ? -1 : null;
      if (offset) {
        for (let i = 0; i < MainPages.length; i++) {
          if (window.location.pathname.startsWith(MainPages[i].path)) {
            const newIdx = (MainPages.length + i + offset) % MainPages.length;
            const newPath = MainPages[newIdx].path;
            history.push(newPath);
            break;
          }
        }
      }
    },
  }) as {ref: RefCallback<Document>};
  useEffect(() => {
    swipeableRef.ref(document);
  }, [swipeableRef]);

  const isWide = useMediaQuery('(min-width: 70em)');
  return (
    <header className={styles.header}>
      <AnchorLink href="/" style={{order: 1}}>
        <img
          src={headerImg}
          alt="Drink Agenda logo"
          width="221px"
          height="65px"
        />
      </AnchorLink>
      <nav
        style={{
          order: isWide ? 1 : 2,
          width: isWide ? 'unset' : '100%',
          marginLeft: 'auto',
          marginRight: isWide ? '0px' : 'auto',
        }}
      >
        <ul>
          <MenuItem page={0} icon={CalendarTwoToneIcon} />
          <MenuItem page={1} icon={LooksTwoToneIcon} />
          <MenuItem page={2} icon={GradeTwoToneIcon} />
          <MenuItem page={3} icon={LightbulbTwoToneIcon} />
          <MenuItem page={4} icon={BarChartTwoToneIcon} />
        </ul>
      </nav>
      <ul>
        <AccountMenuItem />
      </ul>
    </header>
  );
};

export default Header;
